import { useEffect, useRef, useState } from "react";

import {
  flattenMessagePages,
  useConversationMessages,
  useDeescalateConversation,
  useEscalateConversation,
  useFeatureflags,
  useFindOrCreateConversation,
  useMarkAsRead,
  useSendMessage,
  useSessionUser,
} from "@web-app/api";
import useDispatchStore from "@web-app/store/dispatchStore";

import {
  ChatMessageAction,
  ChatMessageAttachment,
  ParticipantWithDetails,
  ProviderState,
} from "../../api/services/chat";

import ChatHeader from "./ChatHeader";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";

interface ChatContainerProps {
  conversationId?: string;
  conversationType?: string;
  driverImage?: string;
  driverName: string;
  onBack?: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  orderAppAvatarUrl?: string;
  orderDisplayId?: string;
  orderExternalId?: string;
  participantsWithDetails?: ParticipantWithDetails[];
  pendingOrderId?: string;
  pendingUserId?: string;
  providerState?: ProviderState;
}

export default function ChatContainer({
  conversationId,
  driverName,
  driverImage,
  orderDisplayId,
  orderExternalId,
  orderAppAvatarUrl,
  participantsWithDetails,
  pendingUserId,
  pendingOrderId,
  conversationType,
  providerState,
  onBack,
  onClose,
  onMinimize,
}: ChatContainerProps) {
  const { data: currentUser } = useSessionUser();
  const [activeConversationId, setActiveConversationId] =
    useState(conversationId);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const isPendingMode = !conversationId && !!pendingUserId;
  const lastMarkedMessageIdRef = useRef<string | null>(null);
  const isAIConversation = participantsWithDetails?.some(
    (p) => p.role === "AI_AGENT",
  );

  const { hasFeature } = useFeatureflags();
  const isWingman = !!isAIConversation && !!hasFeature("wingman");
  const isEscalated =
    providerState?.status === "ESCALATED" ||
    providerState?.status === "PENDING";
  const isAIAnswering = !!isAIConversation && !isEscalated;

  const [dismissedActionMessageIds, setDismissedActionMessageIds] = useState<
    Set<string>
  >(new Set());

  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useConversationMessages(activeConversationId || null);
  const messages = flattenMessagePages(messagesData);

  const isAIThinking = (() => {
    if (!isAIAnswering || messages.length === 0 || !currentUser?.id)
      return false;
    const settledMessages = messages.filter(
      (m) => m.sendingStatus !== "sending",
    );
    if (settledMessages.length === 0) return false;
    const last = settledMessages[settledMessages.length - 1];
    if (last.senderId !== currentUser.id) return false;
    // Bounded by age too: a dead AI run would otherwise leave "Thinking..."
    // on screen forever.
    return Date.now() - new Date(last.createdAt).getTime() < 60_000;
  })();

  const sendMessageMutation = useSendMessage();
  const markAsReadMutation = useMarkAsRead();
  const findOrCreateConversation = useFindOrCreateConversation();
  const escalateConversation = useEscalateConversation();
  const deescalateConversation = useDeescalateConversation();

  const handleMessageAction = (
    messageId: string,
    action: ChatMessageAction,
  ) => {
    if (action.type === "DISMISS") {
      // Session-local: a "not right now", not a stored preference.
      setDismissedActionMessageIds((prev) => new Set(prev).add(messageId));
      return;
    }
    if (action.type === "ESCALATE_TO_SUPPORT" && activeConversationId) {
      escalateConversation.mutate(activeConversationId);
    }
  };

  useEffect(() => {
    lastMarkedMessageIdRef.current = null;
  }, [activeConversationId]);

  useEffect(() => {
    if (isPendingMode && pendingUserId) {
      findOrCreateConversation
        .mutateAsync({
          orderId: pendingOrderId,
          userIds: [pendingUserId],
        })
        .then((conversationDetails) => {
          const newConversationId = conversationDetails.conversation.id;
          setActiveConversationId(newConversationId);
          useDispatchStore.setState({
            activeChatConversationId: newConversationId,
            pendingChatDriverImage: null,
            pendingChatDriverName: null,
            pendingChatOrderDisplayId: null,
            pendingChatOrderId: null,
            pendingChatUserId: null,
          });
        })
        .catch((error) => {
          console.error("Failed to create conversation:", error);
        });
    }
  }, []);

  useEffect(() => {
    if (activeConversationId && messages.length > 0 && currentUser?.id) {
      const unreadMessages = messages.filter(
        (msg) => !msg.isRead && msg.senderId !== currentUser.id,
      );

      if (unreadMessages.length > 0) {
        const lastUnreadId = unreadMessages[unreadMessages.length - 1].id;

        if (lastMarkedMessageIdRef.current !== lastUnreadId) {
          lastMarkedMessageIdRef.current = lastUnreadId;
          markAsReadMutation.mutate({
            conversationId: activeConversationId,
            currentUserId: currentUser.id,
          });
        }
      }
    }
  }, [activeConversationId, messages, currentUser?.id, markAsReadMutation]);

  const handleSendMessage = async (
    content: string,
    attachments: ChatMessageAttachment[],
  ) => {
    if (!currentUser?.id) return;

    if (activeConversationId) {
      sendMessageMutation.mutate(
        {
          attachments,
          content,
          conversationId: activeConversationId,
          currentUserId: currentUser.id,
        },
        {
          onSuccess: () => {
            // Legacy pure-Flex only. Both clauses must stay inside this
            // guard: a CLOSED AI conversation is deliberately back in AI
            // mode, and re-escalating would drag the user into Flex unasked.
            if (conversationType !== "twilioFlex") return;

            const shouldEscalate =
              !providerState || providerState.status === "CLOSED";
            if (shouldEscalate) {
              escalateConversation.mutate(activeConversationId, {
                onSuccess: () => {
                  useDispatchStore.setState({
                    isChatWingmanConversation: false,
                  });
                },
              });
            }
          },
        },
      );
    }
  };

  const handleSearchToggle = () => {
    setIsSearchMode(!isSearchMode);
    if (isSearchMode) {
      setSearchTerm("");
    }
  };

  const handleMessageClick = (messageId: string) => {
    setIsSearchMode(false);
    const hadSearchTerm = searchTerm.trim();
    setSearchTerm("");
    if (hadSearchTerm) {
      setHighlightedMessageId(messageId);
      setTimeout(() => setHighlightedMessageId(null), 2000);
    }
  };

  const handleInitiateCall = () => {
    if (!activeConversationId) {
      return;
    }

    const popupWidth = 400;
    const popupHeight = 600;
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2;

    window.open(
      `/call/${activeConversationId}`,
      "voice-call",
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes`,
    );
  };

  return (
    <div className="flex flex-col h-full bg-white relative">
      <ChatHeader
        canCall={!!activeConversationId && !isPendingMode}
        conversationType={conversationType}
        driverImage={driverImage}
        driverName={driverName}
        isAIConversation={isAIConversation}
        isEscalated={isEscalated}
        isWingman={isWingman}
        isSearchMode={isSearchMode}
        onBack={onBack}
        onClose={onClose}
        onInitiateCall={handleInitiateCall}
        onMinimize={onMinimize}
        onSearchChange={setSearchTerm}
        onSearchToggle={handleSearchToggle}
        orderAppAvatarUrl={orderAppAvatarUrl}
        orderDisplayId={orderDisplayId}
        orderExternalId={orderExternalId}
        searchTerm={searchTerm}
      />
      {isWingman && isEscalated && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100">
          <span className="text-[11px] lg:text-xs text-blue-900">
            You&apos;re chatting with our support team.
          </span>
          <button
            onClick={() =>
              activeConversationId &&
              deescalateConversation.mutate(activeConversationId)
            }
            disabled={deescalateConversation.isPending}
            className="text-[11px] lg:text-xs font-medium text-blue-700 hover:text-blue-900 underline disabled:opacity-50 flex-shrink-0"
          >
            Back to AI assistant
          </button>
        </div>
      )}
      {(isLoading || findOrCreateConversation.isPending) && !messages.length ? (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <p className="text-gray-400 text-xs lg:text-sm">Loading...</p>
        </div>
      ) : (
        <ChatMessages
          messages={messages}
          currentUserId={currentUser?.id || ""}
          currentUserImage={currentUser?.image}
          currentUserName={currentUser?.name || ""}
          otherPartyImage={driverImage}
          otherPartyName={driverName}
          participantsWithDetails={participantsWithDetails}
          isSearchMode={isSearchMode}
          searchTerm={searchTerm}
          onMessageClick={handleMessageClick}
          onMessageAction={handleMessageAction}
          dismissedActionMessageIds={dismissedActionMessageIds}
          isEscalated={isEscalated}
          highlightedMessageId={highlightedMessageId}
          hasMoreMessages={!!hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={fetchNextPage}
          isAIThinking={isAIThinking}
        />
      )}
      <ChatInput
        isAIConversation={isAIConversation}
        onSendMessage={handleSendMessage}
        isLoading={
          sendMessageMutation.isPending ||
          findOrCreateConversation.isPending ||
          isAIThinking
        }
      />
    </div>
  );
}
